import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const colors = ['#3b82f6','#f59e0b','#8b5cf6','#10b981','#ec4899','#06b6d4','#ef4444','#94a3b8'];
const groupBadge = { '시스템관리자':'badge-red','영업관리자':'badge-green','영업담당자':'badge-blue','가격관리자':'badge-orange','뷰어':'badge-gray' };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // user object
  const [showCsv, setShowCsv] = useState(false);
  const [csvData, setCsvData] = useState(null);

  // Form state
  const [form, setForm] = useState({ email:'', password:'', name:'', initials:'', position:'', organization_id:'', group_id:'', language:'ko' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // UID delegation for edit modal
  const [userUidData, setUserUidData] = useState({ own_uid: '', delegated_uids: [] });
  const [availableUserUids, setAvailableUserUids] = useState([]);
  const [selectedUid, setSelectedUid] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (groupFilter) params.set('group', groupFilter);
      if (search) params.set('search', search);
      const data = await api.get(`/users?${params}`);
      setUsers(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [groupFilter, search]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  useEffect(() => {
    Promise.all([api.get('/groups'), api.get('/organizations/tree')])
      .then(([g, o]) => { setGroups(g); setOrgs(flattenOrgs(o)); });
  }, []);

  function flattenOrgs(nodes, result = []) {
    nodes.forEach(n => { result.push({ id: n.id, name: n.name, uid: n.uid }); if (n.children) flattenOrgs(n.children, result); });
    return result;
  }

  const resetForm = () => setForm({ email:'', password:'', name:'', initials:'', position:'', organization_id:'', group_id:'', language:'ko' });

  // Create
  const handleCreate = async () => {
    setFormError('');
    if (!form.email || !form.name) { setFormError('이메일, 이름은 필수입니다.'); return; }
    setFormLoading(true);
    try {
      await api.post('/users', { ...form, organization_id: form.organization_id || null, group_id: form.group_id || null });
      setShowCreate(false); resetForm(); loadUsers();
    } catch (err) { setFormError(err.message); }
    finally { setFormLoading(false); }
  };

  // Edit
  const openEdit = async (user) => {
    setForm({
      email: user.email, password: '', name: user.name, initials: user.initials || '',
      position: user.position || '', organization_id: '', group_id: '',
      language: user.language || 'ko',
    });
    setShowEdit(user);
    setFormError('');
    setSelectedUid('');
    try {
      const [uidData, available] = await Promise.all([
        api.get(`/uid-delegations/by-user/${user.id}`),
        api.get('/uid-delegations/available-uids?type=personal'),
      ]);
      setUserUidData(uidData);
      setAvailableUserUids(available);
    } catch (e) { console.error(e); }
  };

  const addUserUid = async () => {
    if (!selectedUid || !showEdit) return;
    const uidInfo = availableUserUids.find(u => u.uid === selectedUid);
    try {
      await api.post('/uid-delegations', {
        target_uid: selectedUid, uid_type: 'personal',
        original_owner: uidInfo?.owner_name || '', delegate_type: 'user', delegate_user_id: showEdit.id,
      });
      setSelectedUid('');
      const uidData = await api.get(`/uid-delegations/by-user/${showEdit.id}`);
      setUserUidData(uidData);
    } catch (e) { alert(e.message); }
  };

  const removeUserUid = async (delegationId) => {
    try {
      await api.del(`/uid-delegations/${delegationId}`);
      const uidData = await api.get(`/uid-delegations/by-user/${showEdit.id}`);
      setUserUidData(uidData);
    } catch (e) { alert(e.message); }
  };

  const handleEdit = async () => {
    setFormError(''); setFormLoading(true);
    try {
      const body = { name: form.name, initials: form.initials || null, position: form.position || null, language: form.language };
      if (form.organization_id) body.organization_id = parseInt(form.organization_id);
      if (form.group_id) body.group_id = parseInt(form.group_id);
      if (form.new_password) body.new_password = form.new_password;
      await api.put(`/users/${showEdit.id}`, body);
      setShowEdit(null); resetForm(); loadUsers();
    } catch (err) { setFormError(err.message); }
    finally { setFormLoading(false); }
  };

  // Toggle status
  const toggleStatus = async (user) => {
    try {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      await api.put(`/users/${user.id}`, { status: newStatus });
      loadUsers();
    } catch (err) { alert(err.message); }
  };

  // CSV
  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) { alert('데이터가 없습니다.'); return; }
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1).map(line => {
        const vals = line.split(',').map(v => v.trim());
        const obj = {};
        headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
        // 검증
        obj._status = '✓';
        obj._issue = '';
        if (!obj.name || !obj.email) { obj._status = '✕'; obj._issue = '이름/이메일 누락'; }
        else if (!obj.email.includes('@')) { obj._status = '✕'; obj._issue = '이메일 형식 오류'; }
        obj.action = 'create'; // default
        return obj;
      });
      // 중복 체크
      const emails = users.map(u => u.email);
      rows.forEach(r => {
        if (r._status === '✕') return;
        if (emails.includes(r.email)) { r._status = '⚠'; r._issue = '이메일 중복 (기존 사용자)'; r.action = 'skip'; }
      });
      setCsvData(rows);
    };
    reader.readAsText(file);
  };

  const handleCsvImport = async () => {
    const toImport = csvData.filter(r => r._status !== '✕').map(r => ({
      email: r.email, name: r.name, initials: r.initials, position: r.position,
      organization_name: r.organization, group_name: r.group, language: r.language || 'ko',
      password: 'password123', action: r.action === 'skip' ? 'skip' : r.action === 'overwrite' ? 'overwrite' : undefined,
    }));
    try {
      const result = await api.post('/users/bulk', { users: toImport });
      alert(`등록: ${result.created}건, 덮어쓰기: ${result.overwritten}건, 건너뛰기: ${result.skipped}건, 오류: ${result.errors}건`);
      setShowCsv(false); setCsvData(null); loadUsers();
    } catch (err) { alert(err.message); }
  };

  const downloadSampleCsv = () => {
    const csv = 'name,email,initials,position,organization,group,language\n김민수,min@soosan.com,KMS,사원,영업1팀,영업담당자,ko\n이수진,sj@soosan.com,LSJ,대리,영업2팀,영업담당자,ko';
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'users_sample.csv'; a.click();
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => loadUsers(), 300);
    return () => clearTimeout(timer);
  }, [search, loadUsers]);

  return (
    <div>
      {/* Toolbar */}
      <div className="toolbar">
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <div className="search-box">
            <span>🔍</span>
            <input placeholder="이름, 이메일, UID 검색..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-input" style={{ width:130, padding:'7px 10px', fontSize:12 }} value={groupFilter} onChange={e => setGroupFilter(e.target.value)}>
            <option value="">전체 그룹</option>
            {groups.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
        </div>
        <div className="btn-group">
          <button className="btn btn-outline btn-sm" onClick={() => { setShowCsv(true); setCsvData(null); }}>📄 CSV 일괄 등록</button>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowCreate(true); resetForm(); setFormError(''); }}>✚ 사용자 등록</button>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center' }}>로딩 중...</div> : (
          <div style={{ overflowX:'auto' }}>
            <table>
              <thead><tr><th>이름</th><th>UID</th><th>이메일</th><th>소속</th><th>직책</th><th>그룹</th><th>이니셜</th><th>언어</th><th>상태</th><th>마지막 로그인</th><th>작업</th></tr></thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.uid}>
                    <td><div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ width:30, height:30, background:colors[i%colors.length], borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#fff', fontWeight:600 }}>{u.name[0]}</div>
                      <span className="fw-bold">{u.name}</span>
                    </div></td>
                    <td className="text-muted">{u.uid}</td>
                    <td>{u.email}</td>
                    <td>{u.org_name || '-'}</td>
                    <td>{u.position || '-'}</td>
                    <td><span className={`badge ${groupBadge[u.group_name]||'badge-gray'}`}>{u.group_name||'-'}</span></td>
                    <td style={{ fontFamily:'monospace', fontWeight:700 }}>{u.initials||'-'}</td>
                    <td>{u.language==='ko'?'한국어':u.language==='en'?'English':u.language}</td>
                    <td><span style={{ color:u.status==='active'?'#22c55e':'#94a3b8' }}>●</span> {u.status==='active'?'활성':'비활성'}</td>
                    <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString('ko',{month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'}) : '-'}</td>
                    <td><div className="btn-group">
                      {u.uid === 'U-0000' ? (
                        <span style={{ fontSize:11, color:'#94a3b8' }}>수정 불가</span>
                      ) : (
                        <>
                          <button className="btn btn-outline btn-sm" onClick={() => openEdit(u)}>편집</button>
                          <button className={`btn btn-sm ${u.status==='active'?'btn-danger':'btn-success'}`} onClick={() => toggleStatus(u)}>
                            {u.status==='active'?'비활성':'활성화'}
                          </button>
                        </>
                      )}
                    </div></td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={11} style={{ textAlign:'center', padding:32, color:'#94a3b8' }}>검색 결과가 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== CREATE MODAL ===== */}
      {showCreate && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:520, maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>사용자 등록</span>
              <span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => setShowCreate(false)}>✕</span>
            </div>
            {formError && <div className="alert alert-danger" style={{ marginBottom:12 }}>{formError}</div>}
            <div className="form-row">
              <div className="form-group" style={{ flex:1 }}><label className="form-label">이름 *</label><input className="form-input" value={form.name} onChange={e => setForm({...form, name:e.target.value})} /></div>
              <div className="form-group" style={{ flex:1 }}><label className="form-label">이메일 *</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})} /></div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex:1 }}><label className="form-label">초기 비밀번호</label><input className="form-input" value="1234" disabled style={{ background:'#f8fafc' }} /><div className="form-hint">최초 로그인 시 변경 필수</div></div>
              <div className="form-group" style={{ flex:1 }}><label className="form-label">고유 이니셜</label><input className="form-input" value={form.initials} onChange={e => setForm({...form, initials:e.target.value.toUpperCase()})} placeholder="예: KCS" maxLength={5} /></div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex:1 }}><label className="form-label">직책</label><input className="form-input" value={form.position} onChange={e => setForm({...form, position:e.target.value})} placeholder="예: 대리" /></div>
              <div className="form-group" style={{ flex:1 }}><label className="form-label">언어</label>
                <select className="form-input" value={form.language} onChange={e => setForm({...form, language:e.target.value})}><option value="ko">한국어</option><option value="en">English</option></select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex:1 }}><label className="form-label">소속 조직</label>
                <select className="form-input" value={form.organization_id} onChange={e => setForm({...form, organization_id:e.target.value})}>
                  <option value="">선택</option>
                  {orgs.map(o => <option key={o.id} value={o.id}>{o.name} ({o.uid})</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex:1 }}><label className="form-label">사용자 그룹</label>
                <select className="form-input" value={form.group_id} onChange={e => setForm({...form, group_id:e.target.value})}>
                  <option value="">선택</option>
                  {groups.filter(g => g.name !== '시스템관리자').map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
              <button className="btn btn-outline" onClick={() => setShowCreate(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={formLoading}>{formLoading?'등록 중...':'등록'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== EDIT MODAL ===== */}
      {showEdit && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:520, maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>사용자 편집 — {showEdit.name} ({showEdit.uid})</span>
              <span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => setShowEdit(null)}>✕</span>
            </div>
            {formError && <div className="alert alert-danger" style={{ marginBottom:12 }}>{formError}</div>}
            <div className="form-row">
              <div className="form-group" style={{ flex:1 }}><label className="form-label">이름</label><input className="form-input" value={form.name} onChange={e => setForm({...form, name:e.target.value})} /></div>
              <div className="form-group" style={{ flex:1 }}><label className="form-label">이메일</label><input className="form-input" value={form.email} disabled style={{ background:'#f8fafc' }} /></div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex:1 }}><label className="form-label">고유 이니셜</label><input className="form-input" value={form.initials} onChange={e => setForm({...form, initials:e.target.value.toUpperCase()})} maxLength={5} /></div>
              <div className="form-group" style={{ flex:1 }}><label className="form-label">직책</label><input className="form-input" value={form.position} onChange={e => setForm({...form, position:e.target.value})} /></div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex:1 }}><label className="form-label">소속 조직</label>
                <select className="form-input" value={form.organization_id} onChange={e => setForm({...form, organization_id:e.target.value})}>
                  <option value="">변경 안 함</option>
                  {orgs.map(o => <option key={o.id} value={o.id}>{o.name} ({o.uid})</option>)}
                </select>
              </div>
              <div className="form-group" style={{ flex:1 }}><label className="form-label">사용자 그룹</label>
                <select className="form-input" value={form.group_id} onChange={e => setForm({...form, group_id:e.target.value})}>
                  <option value="">변경 안 함</option>
                  {groups.filter(g => g.name !== '시스템관리자').map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex:1 }}><label className="form-label">언어</label>
                <select className="form-input" value={form.language} onChange={e => setForm({...form, language:e.target.value})}><option value="ko">한국어</option><option value="en">English</option></select>
              </div>
            </div>
            <div style={{ borderTop:'1px solid #e2e8f0', marginTop:12, paddingTop:12 }}>
              <div className="form-group">
                <label className="form-label">비밀번호 재설정 <span style={{ fontWeight:400, color:'#94a3b8' }}>(입력 시 변경, 비우면 유지)</span></label>
                <input className="form-input" type="password" value={form.new_password || ''} onChange={e => setForm({...form, new_password:e.target.value})} placeholder="새 비밀번호 (4자리 이상)" />
                {form.new_password && <div className="form-hint" style={{ color:'#f59e0b' }}>⚠ 비밀번호 지정 시 해당 사용자는 다음 로그인에서 비밀번호 변경이 필수가 됩니다.</div>}
              </div>
            </div>
            {/* UID 관리 */}
            <div style={{ borderTop:'1px solid #e2e8f0', marginTop:12, paddingTop:12 }}>
              <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>개인 UID 관리</div>
              <div style={{ fontSize:12, marginBottom:8 }}>자체 UID: <span className="badge badge-blue">{userUidData.own_uid}</span></div>
              {userUidData.delegated_uids.length > 0 && (
                <table style={{ marginBottom:10, fontSize:12 }}>
                  <thead><tr><th>위임 UID</th><th>원래 소유</th><th></th></tr></thead>
                  <tbody>
                    {userUidData.delegated_uids.map(d => (
                      <tr key={d.id}>
                        <td><span className="badge badge-blue">{d.target_uid}</span></td>
                        <td>{d.original_owner}</td>
                        <td><button className="btn btn-danger btn-sm" onClick={() => removeUserUid(d.id)}>삭제</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div style={{ display:'flex', gap:8 }}>
                <select className="form-input" style={{ flex:1 }} value={selectedUid} onChange={e => setSelectedUid(e.target.value)}>
                  <option value="">UID 선택...</option>
                  {availableUserUids
                    .filter(u => u.uid !== userUidData.own_uid && !userUidData.delegated_uids.some(d => d.target_uid === u.uid))
                    .map(u => <option key={u.uid} value={u.uid}>{u.uid} — {u.owner_name} ({u.status === 'active' ? '활성' : '비활성'})</option>)}
                </select>
                <button className="btn btn-primary btn-sm" disabled={!selectedUid} onClick={addUserUid}>추가</button>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
              <button className="btn btn-outline" onClick={() => setShowEdit(null)}>취소</button>
              <button className="btn btn-primary" onClick={handleEdit} disabled={formLoading}>{formLoading?'저장 중...':'저장'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CSV MODAL ===== */}
      {showCsv && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, width:750, maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>사용자 일괄 등록 (CSV)</span>
              <span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={() => { setShowCsv(false); setCsvData(null); }}>✕</span>
            </div>

            {!csvData ? (
              <>
                <div style={{ fontSize:14, fontWeight:700, marginBottom:10 }}>1. CSV 파일 업로드</div>
                <div style={{ border:'2px dashed #d1d5db', borderRadius:10, padding:32, textAlign:'center', cursor:'pointer', color:'#94a3b8', position:'relative' }}>
                  <div style={{ fontSize:28, marginBottom:8 }}>📄</div>
                  파일을 선택하여 업로드 (.csv)
                  <input type="file" accept=".csv" onChange={handleCsvFile}
                    style={{ position:'absolute', inset:0, opacity:0, cursor:'pointer' }} />
                </div>
                <div style={{ marginTop:10 }}>
                  <button className="btn btn-outline btn-sm" onClick={downloadSampleCsv}>📥 샘플 CSV 다운로드</button>
                </div>
                <div className="text-muted" style={{ marginTop:8 }}>
                  CSV 컬럼: name, email, initials, position, organization, group, language
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize:14, fontWeight:700, marginBottom:10 }}>2. 미리보기 / 검증 결과</div>
                <div style={{ marginBottom:10, fontSize:13 }}>
                  전체 {csvData.length}건:
                  <span style={{ color:'#22c55e', fontWeight:700, marginLeft:8 }}>✓ {csvData.filter(r=>r._status==='✓').length}</span>
                  <span style={{ color:'#f59e0b', fontWeight:700, marginLeft:8 }}>⚠ {csvData.filter(r=>r._status==='⚠').length}</span>
                  <span style={{ color:'#ef4444', fontWeight:700, marginLeft:8 }}>✕ {csvData.filter(r=>r._status==='✕').length}</span>
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ fontSize:12 }}>
                    <thead><tr><th>상태</th><th>이름</th><th>이메일</th><th>이니셜</th><th>조직</th><th>그룹</th><th>처리</th><th>비고</th></tr></thead>
                    <tbody>
                      {csvData.map((r, i) => (
                        <tr key={i} style={{ background: r._status==='✕'?'#fef2f2':r._status==='⚠'?'#fffbeb':'transparent' }}>
                          <td style={{ fontWeight:700, color: r._status==='✓'?'#22c55e':r._status==='⚠'?'#f59e0b':'#ef4444' }}>{r._status}</td>
                          <td>{r.name || <span style={{ color:'#ef4444' }}>(누락)</span>}</td>
                          <td>{r.email}</td>
                          <td>{r.initials||'-'}</td>
                          <td>{r.organization||'-'}</td>
                          <td>{r.group||'-'}</td>
                          <td>
                            {r._status === '⚠' && (
                              <select style={{ padding:'2px 6px', fontSize:11, border:'1px solid #d1d5db', borderRadius:4 }}
                                value={r.action} onChange={e => { const d=[...csvData]; d[i].action=e.target.value; setCsvData(d); }}>
                                <option value="skip">건너뛰기</option>
                                <option value="overwrite">덮어쓰기</option>
                              </select>
                            )}
                            {r._status === '✓' && '신규 등록'}
                            {r._status === '✕' && '제외'}
                          </td>
                          <td style={{ fontSize:11, color:r._status==='⚠'?'#f59e0b':r._status==='✕'?'#ef4444':'#94a3b8' }}>{r._issue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display:'flex', gap:8, justifyContent:'space-between', marginTop:16 }}>
                  <button className="btn btn-outline" onClick={() => setCsvData(null)}>다시 업로드</button>
                  <div className="btn-group">
                    {csvData.filter(r=>r._status==='⚠').length > 0 && (
                      <>
                        <button className="btn btn-outline btn-sm" onClick={() => { const d=csvData.map(r=>r._status==='⚠'?{...r,action:'skip'}:r); setCsvData(d); }}>전체 건너뛰기</button>
                        <button className="btn btn-outline btn-sm" onClick={() => { const d=csvData.map(r=>r._status==='⚠'?{...r,action:'overwrite'}:r); setCsvData(d); }}>전체 덮어쓰기</button>
                      </>
                    )}
                    <button className="btn btn-primary" onClick={handleCsvImport}>
                      등록 ({csvData.filter(r=>r._status!=='✕').length}건)
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
